<script setup lang="ts">
import { useSettingsNav } from '../lib/settingsNav'
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { useSession } from '../stores/session'
import { usePulls } from '../stores/pulls'
import { useUnread } from '../stores/unread'
import { computed } from 'vue'
import Avatar from './Avatar.vue'
import SearchModal from './SearchModal.vue'
import { onBeforeUnmount, onMounted } from 'vue'

defineProps<{ activeSpace: string | null; dmMode: boolean; libraryMode?: boolean; serverMode?: boolean }>()
const emit = defineEmits<{ 'create-space': [] }>()
const session = useSession()
const router = useRouter()
const searching = ref(false)
const { open: openSettings } = useSettingsNav()
function onKey(e: KeyboardEvent): void {
  if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
    e.preventDefault()
    searching.value = true
  }
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
const unread = useUnread()
const pulls = usePulls()
const dmUnread = computed(() => session.dms.reduce((n, c) => n + (unread.counts[c.id] ?? 0), 0))
</script>

<template>
  <nav class="flex h-full w-[60px] shrink-0 flex-col items-center border-r border-border bg-bg-primary" aria-label="Spaces">
    <button
      class="mono relative flex h-14 w-full items-center justify-center border-b border-border tracking-[0.12em] transition-colors"
      :class="dmMode ? 'bg-ink text-paper' : 'text-text-muted hover:text-text-primary'"
      title="Direct messages"
      data-testid="nav-dms"
      @click="router.push({ name: 'dm' })"
    >
      DM
      <span v-if="dmUnread" class="badge absolute top-2 right-1.5" data-testid="dm-unread">{{ dmUnread }}</span>
    </button>
    <button
      class="mono relative flex h-14 w-full items-center justify-center border-b border-border tracking-[0.12em] transition-colors"
      :class="libraryMode ? 'bg-ink text-paper' : 'text-text-muted hover:text-text-primary'"
      :title="pulls.total ? `Library · ${pulls.total} pull request${pulls.total === 1 ? '' : 's'} with news` : 'Library'"
      data-testid="nav-library"
      @click="router.push({ name: 'library' })"
    >
      LIB
      <span v-if="pulls.total" class="badge absolute top-2 right-1.5" data-testid="library-unread">{{ pulls.total }}</span>
    </button>
    <button
      v-if="session.isAdmin"
      class="mono flex h-14 w-full items-center justify-center border-b border-border tracking-[0.12em] transition-colors"
      :class="serverMode ? 'bg-ink text-paper' : 'text-text-muted hover:text-text-primary'"
      title="Server settings"
      data-testid="nav-server"
      @click="router.push({ name: 'server' })"
    >
      SRV
    </button>
    <div class="scroll-thin flex w-full flex-1 flex-col items-center gap-3 overflow-y-auto py-3">
      <button
        v-for="s in session.spaces"
        :key="s.id"
        class="relative p-0.5 transition-opacity"
        :class="s.id === activeSpace ? 'outline outline-1 outline-ink' : ''"
        :title="s.name"
        :aria-current="s.id === activeSpace ? 'page' : undefined"
        :data-testid="`space-${s.name}`"
        @click="router.push({ name: 'space', params: { spaceId: s.id } })"
      >
        <Avatar :id="s.id" :name="s.name" :size="36" square :pixels="s.icon" />
        <span
          v-if="unread.spaceUnread(s.channels.map((c) => c.id)) && s.id !== activeSpace"
          class="absolute -top-2 -right-2 h-2 w-2 bg-signal"
          :data-testid="`space-unread-${s.name}`"
        />
      </button>
      <button
        v-if="session.canCreateSpaces"
        class="flex h-10 w-10 items-center justify-center border border-dashed border-border-strong text-lg font-light text-text-muted transition-colors hover:border-ink hover:text-text-primary"
        title="Create a space"
        data-testid="create-space"
        @click="emit('create-space')"
      >
        +
      </button>
    </div>
    <button
      class="flex h-11 w-full items-center justify-center border-t border-border text-text-muted hover:text-text-primary"
      title="Search (Ctrl+K)"
      aria-label="Search"
      data-testid="open-search"
      @click="searching = true"
    >
      <svg viewBox="0 0 16 16" class="h-4 w-4" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
        <circle cx="6.75" cy="6.75" r="4.75" />
        <path d="M10.25 10.25L14.5 14.5" />
      </svg>
    </button>
    <button class="flex h-14 w-full items-center justify-center border-t border-border" title="Settings" data-testid="open-settings" @click="openSettings()">
      <Avatar
        :id="session.me ?? ''"
        :name="session.profiles.get(session.me ?? '')?.display_name ?? session.me ?? '?'"
        :size="28"
      />
    </button>
    <SearchModal v-if="searching" @close="searching = false" />
  </nav>
</template>
